import request from '@/config/axios'

// 竞价机构订单 - 列表项，字段以后端 PatentBiddingOrderRespVO 为准
export interface PatentBiddingOrderRespVO {
  id: number // 订单ID
  patentId: number | string // 专利业务ID(雪花)。19 位超出 JS 安全整数范围，按字符串承载
  patentName: string // 专利名称
  patentType: string // 专利类型码：0-发明,1-实用新型,2-外观设计,3-发明PCT,4-实用新型PCT
  patentTypeLabel: string // 专利类型文本
  applicationNumber: string // 申请号
  caseStatus: string // 案件状态（自由文本，衔接自提案完成）
  initiator: string // 委托人（发起人姓名）
  patentTenantId: number // 委托单位（专利所属高校租户）ID
  patentTenantName: string // 委托单位名称
  agencyCost: number // 代理服务费(元)
  status: number // 订单状态码：0-待接单,1-已结单,2-已拒绝
  statusLabel: string // 订单状态文本
  proposalStatus: string // 提案状态码
  proposalStatusLabel: string // 处理状态文本：待查新/待立案/校内审批中
  reason: string // 拒单原因
  handlerTime: string // 处理时间（接单/拒单动作发生时间）
  handlerName: string // 处理人姓名
  reviewUserName: string // 查新人姓名
  reviewTime: string // 查新通过时间
  filingUserName: string // 立案人姓名
  filingTime: string // 立案通过时间
  payStatus: number // 收款状态码：0-待收款,1-已收款
  payStatusLabel: string // 收款状态文本
  paidUserName: string // 收款确认人姓名
  paidTime: string // 收款时间
  createTime: string // 下单时间
}

// 分页返回
export interface PageResult<T> {
  total: number
  list: T[]
}

// 订单分页查询请求参数，字段以后端 BiddingOrderPageReqVO 为准
export interface BiddingOrderPageReqVO {
  pageNo: number // 页码，从 1 开始（必填）
  pageSize: number // 每页条数，最大 200（必填）
  searchMode: 'pending' | 'handled' | 'rejected' | 'receipt' // 列表类型：待接单/已接单/已拒单/回执订单（必填）
  tab?: 'pending' | 'internal' | '' // 处理列表页签(仅 handled 生效)：待处理/校内审批中/不传=全部
  patentTenantName?: string // 客户名称（委托单位名称，模糊）
  patentName?: string // 专利名称（模糊）
  applicationNumber?: string // 申请号（模糊）
  patentType?: string // 专利类型：0-发明,1-实用新型,2-外观设计,3-发明PCT,4-实用新型PCT
  initiator?: string // 委托人姓名（发起人，模糊）
  statusLabel?: string // 案件状态（模糊，后端为自由文本）
  payStatus?: number // 收款状态：0-待收款,1-已收款
  patentTenantId?: number // 委托单位（专利所属高校租户ID）
  createTimeStart?: string // 下单时间-起始
  createTimeEnd?: string // 下单时间-截止
}

// patentId 是 19 位雪花 ID，超出 JS Number 的安全整数范围（2^53-1，16 位），
// 直接 JSON.parse 会把 1930259081650421764 读成 1930259081650421800。
// 这里在解析前把 17 位以上的裸整数转成字符串字面量，只影响长整数：
// 小数、短整数（id/status 等）、字符串内部的数字都不受影响。
const parseWithBigIntSafe = (raw: string) => {
  if (typeof raw !== 'string' || !raw) return raw
  try {
    return JSON.parse(raw.replace(/:\s*(-?\d{17,})(?=\s*[,}])/g, ':"$1"'))
  } catch {
    return raw // 非 JSON（如后端返回 HTML 错误页）时交回给 axios 默认处理
  }
}

// 竞价机构订单分页
export const getBiddingOrderPage = (data: BiddingOrderPageReqVO) => {
  return request.post<PageResult<PatentBiddingOrderRespVO>>({
    url: '/patent/bidding/order/page',
    data,
    // 保住返回列表里的 patentId，否则确认收款会提交一个失真的 ID
    transformResponse: [parseWithBigIntSafe]
  })
}

// 附件项，字段以后端 PatentAttachmentCreateReqVO 为准
export interface PatentAttachmentCreateReqVO {
  fileDescr: string // 附件文件分类（同一分类重复上传会覆盖）
  fileName: string // 附件文件名
  filePath: string // 附件存储URL
  fileSize?: number // 文件大小（字节）
}

// 上传订单附件请求参数，字段以后端 BiddingAttachmentSaveReqVO 为准
export interface BiddingAttachmentSaveReqVO {
  id: number // 竞价订单ID
  attachmentList: PatentAttachmentCreateReqVO[] // 附件列表（必填，按 fileDescr 分类 upsert，同分类覆盖）
}

// 竞价机构维护订单附件：仅维护附件、不推进流程（补传/更正材料）。
// 按 fileDescr 分类 upsert，同分类覆盖。随过闸一并上传请走 approveBiddingOrder（与过闸同事务）
export const saveBiddingOrderAttachment = (data: BiddingAttachmentSaveReqVO) => {
  return request.post<number>({
    url: '/patent/bidding/order/save-attachment',
    data
  })
}

// 推进动作：查新通过 / 立案通过 / 驳回
export type BiddingApproveAction = 'reviewPass' | 'filingPass' | 'reject'

// 推进流程请求参数，字段以后端 BiddingGateReqVO 为准
export interface BiddingGateReqVO {
  id: number // 竞价订单ID
  action: BiddingApproveAction // 推进动作
  opinion: string // 审批意见
  applicationNumber?: string // 专利申请号（仅 filingPass 必填，回填 patent 主表）
  rejectTarget?: string // 驳回目标（仅 reject 必填）
  returnMode?: RejectReturnMode // 驳回后的审核方式（仅 reject 有效，不传按逐级）
  attachmentList?: PatentAttachmentCreateReqVO[] // 附件列表（可选，与过闸同事务）
}

// 驳回后的审核方式：direct-直接本环节复核，sequential-按原流程逐级重审
export type RejectReturnMode = 'direct' | 'sequential'

// 可驳回目标，字段以后端 PatentRejectTargetRespVO 为准
export interface PatentRejectTargetRespVO {
  rejectTarget: string // 驳回目标（提交时回传）
  nodeKey: string // 节点 key
  nodeName: string // 节点名称
}

// 驳回弹窗可选的退回节点（仅已接单、处于查新/立案节点的订单）
export const getBiddingRejectTargets = (id: number) => {
  return request.get<PatentRejectTargetRespVO[]>({
    url: '/patent/bidding/reject-targets',
    params: { id }
  })
}

// 竞价机构推进流程（过闸）。带 attachmentList 时附件与状态流转在同一事务内完成
export const approveBiddingOrder = (data: BiddingGateReqVO) => {
  return request.post<boolean>({
    url: '/patent/bidding/approve',
    data
  })
}

// 确认收款请求参数，字段以后端 BiddingConfirmPayReqVO 为准
export interface BiddingConfirmPayReqVO {
  // 专利业务ID(雪花)=patent.patent_id。
  // 雪花 ID 为 19 位，超出 JS Number.MAX_SAFE_INTEGER(16 位)，用 string 承载避免精度丢失
  patentId: number | string
}

// 竞价机构确认收款：已接单订单 待收款→已收款。后端乐观更新保幂等，重复确认不覆盖首次收款人
// 用 transformRequest 手动拼 JSON，让 patentId 以数字字面量形式原样发出，
// 不经过 JSON.stringify 对 number 的处理，避免 19 位雪花 ID 被写成失真的值
export const confirmBiddingOrderPay = (data: BiddingConfirmPayReqVO) => {
  return request.post<boolean>({
    url: '/patent/bidding/order/confirm-pay',
    data,
    transformRequest: [() => `{"patentId":${String(data.patentId)}}`]
  })
}

// 接单/拒单动作
export type BiddingHandleOperationType = 'accept' | 'reject'

// 接单/拒单请求参数，字段以后端 PatentBiddingOrderHandleReqVO 为准
export interface PatentBiddingOrderHandleReqVO {
  id: number // 订单ID（必填）
  operationType: BiddingHandleOperationType // 处理动作：accept-接单,reject-拒单（必填）
  reason?: string // 拒单原因（仅 reject 需要）
}

// 竞价机构接单/拒单：接单同时启动专利审批流程；拒单置专利为「重新选择代理」
export const handleBiddingOrder = (data: PatentBiddingOrderHandleReqVO) => {
  return request.post<boolean>({
    url: '/patent/bidding/order/handle',
    data
  })
}

// 附件项（详情返回），字段以后端 PatentAttachmentRespVO 为准
export interface PatentAttachmentRespVO {
  id: number // 附件ID
  patentId: number | string // 专利ID
  fileName: string // 附件文件名
  filePath: string // 附件存储URL
  fileSize: number // 文件大小（字节）
  fileDescr: string // 文件描述（即附件分类）
  uploadDate: string // 上传日期
  uploaderId: number // 上传人ID
  uploaderName: string // 上传人名称
  createTime: string // 创建时间
}

// 流程节点，字段以后端 BiddingOrderFlowRespVO 为准
export interface BiddingOrderFlowRespVO {
  nodeName: string // 节点名称
  auditOpinion: string // 处理意见（未处理的占位节点为空）
  handleTime: string // 处理时间（未处理的占位节点为空）
}

// 发明人项，字段以后端 PatentInventorRespVO 为准
export interface PatentInventorRespVO {
  id: number // 发明人ID
  patentId: number | string // 专利ID
  inventorId: number // 发明人Id（为 0/空表示校外人员）
  inventorName: string // 发明人姓名
  splicingName: string // 发明人姓名（拼接展示用）
  inventorOrder: number // 发明人位序
  email: string // 邮箱
  phone: string // 手机号
  idCardNumber: string // 身份证号码
  isResponsible: string // 是否负责人
  createTime: string // 创建时间
}

// 订单详情返回，字段以后端 BiddingOrderDetailRespVO 为准
export interface BiddingOrderDetailRespVO {
  id: number // 订单ID
  patentId: number | string // 专利业务ID(雪花)，按字符串承载防精度丢失
  patentName: string // 专利名称
  patentType: string // 专利类型码
  patentTypeLabel: string // 专利类型文本
  status: number // 订单状态码：0-待接单,1-已结单,2-已拒绝
  statusLabel: string // 订单状态文本
  initiator: string // 委托人（发起人姓名）
  contactMobile: string // 委托人联系电话（实时取自用户中心，非快照）
  inventor: string // 所有发明人（逗号分隔，按序号）
  patentTenantName: string // 委托单位（高校）名称
  patentNum: string // 专利编号
  applicationNumber: string // 申请号
  proposalContent: string // 提案内容
  field: string // 技术领域
  proposalStatus: string // 提案状态码
  proposalStatusLabel: string // 提案状态文本
  collegeName: string // 学院名称
  personInChargeUserName: string // 负责人姓名
  attachmentList: PatentAttachmentRespVO[] // 附件列表
  flowList: BiddingOrderFlowRespVO[] // 流程信息
  inventorList: PatentInventorRespVO[] // 发明人列表
}

// 竞价机构订单详情：入参订单ID，一次返回基础信息 + 四个 Tab 的数据。
// 同样用 transformResponse 保住 19 位的 patentId，避免精度丢失
export const getBiddingOrderDetail = (id: number) => {
  return request.get<BiddingOrderDetailRespVO>({
    url: '/patent/bidding/order/detail',
    params: { id },
    transformResponse: [parseWithBigIntSafe]
  })
}

// 导出发明人名单：入参订单ID，返回 Excel 二进制流（身份/姓名/手机号/邮箱/身份证号）
// 用 request.download，它内部走 responseType: 'blob'，拿到的是原始 Blob 而非 res.data
export const exportBiddingOrderInventorList = (id: number) => {
  return request.download({ url: '/patent/bidding/order/export-inventor-list', params: { id } })
}
